// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#collection-cadence
import * as React from 'react';

import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { cn } from '@/lib/utils';
import {
  isUpstreamImpaired,
  type UpstreamStatus,
  type UpstreamStatusEntry,
  type UpstreamStatusState,
} from '@greenlight/shared';

// Status word plus a dot, so state is carried by text and shape rather than
// colour alone (WCAG 2.1 AA) — the same pairing the cloud resource tiles use.
const STATE_WORD: Record<UpstreamStatusState, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  major: 'Major outage',
  unknown: 'Not reporting',
};

const STATE_DOT_CLASS: Record<UpstreamStatusState, string> = {
  operational: 'bg-[hsl(var(--status-green))]',
  degraded: 'bg-[hsl(var(--status-amber))]',
  major: 'bg-[hsl(var(--status-red))]',
  unknown: 'bg-slate/40',
};

interface UpstreamStatusSectionProps {
  upstream: UpstreamStatus | undefined;
}

/**
 * The one-line summary. `unknown` is absence of signal, not good news, so it gets
 * its own line rather than folding into "all normal" — a page whose every row
 * reads Not reporting must not summarise as healthy, which is the same fail-quiet
 * rule the checker follows when it declines to call an unreachable page an outage.
 */
function summarize(upstream: UpstreamStatus): string {
  const impaired = upstream.filter((entry) => isUpstreamImpaired(entry.state)).length;
  if (impaired > 0) return `${impaired} reporting problems`;
  const unknown = upstream.filter((entry) => entry.state === 'unknown').length;
  if (unknown > 0) return `${unknown} not reporting`;
  return 'All reporting normally';
}

/**
 * One vendor, as a tile: mark, name, state, and when it was last checked. A
 * healthy vendor is four short lines, because the card is scanned for the one
 * row that is not green and read no further.
 */
function UpstreamTile({ entry }: { readonly entry: UpstreamStatusEntry }): React.JSX.Element {
  return (
    <div className="bg-card px-4 py-3" data-testid={`upstream-row-${entry.key}`}>
      <div className="flex min-w-0 items-center gap-2">
        <IntegrationTile slug={entry.key} displayName={entry.label} size={18} />
        <span className="min-w-0 truncate text-[13px] font-medium text-foreground">
          {entry.label}
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <span
          className={cn('size-1.5 shrink-0 rounded-full', STATE_DOT_CLASS[entry.state])}
          aria-hidden="true"
        />
        <span className="text-[13px] text-foreground">{STATE_WORD[entry.state]}</span>
      </div>
      {/* The vendor's own words only when they are not "everything is fine":
          five copies of "All Systems Operational" is the noise this card had. */}
      {isUpstreamImpaired(entry.state) && entry.description !== null ? (
        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{entry.description}</p>
      ) : null}
      <p className="mt-1 text-[11px] text-muted-foreground">
        {/* Greenlight observing a vendor directly is the exception; the card
            states the rule once for everything else. */}
        {entry.signal === 'observed' ? 'as observed by Greenlight · ' : ''}
        {relativeTime(entry.last_checked_at)}
      </p>
    </div>
  );
}

/**
 * Upstream vendors on the org-admin health page, so IT can tell "their agent is
 * down" from "Greenlight is down" without opening five status pages.
 *
 * Greenlight never calls the agent vendors on anyone's behalf, so their status
 * page is the only thing we know, and saying so once in the UI is the honest
 * version of reporting it at all.
 */
export function UpstreamStatusSection({ upstream }: UpstreamStatusSectionProps): React.JSX.Element {
  return (
    <SectionCard
      title="Upstream services"
      badge={
        upstream && upstream.length > 0 ? (
          <span className="text-xs text-muted-foreground">{summarize(upstream)}</span>
        ) : undefined
      }
      className="md:col-span-2"
    >
      {!upstream || upstream.length === 0 ? (
        <p className="text-sm text-muted-foreground">No upstream status in the latest snapshot.</p>
      ) : (
        <>
          <p className="mb-3 text-xs text-muted-foreground">
            The agent and source-control vendors your org depends on, read from each vendor&rsquo;s
            own status page unless a tile says otherwise &mdash; Greenlight never calls these
            vendors on your behalf.
          </p>
          <div
            className="overflow-hidden rounded-[8px] border border-border bg-border"
            data-testid="upstream-status-list"
          >
            <div className="grid grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-4">
              {upstream.map((entry) => (
                <UpstreamTile key={entry.key} entry={entry} />
              ))}
              {Array.from({ length: (4 - (upstream.length % 4)) % 4 }).map((_, i) => (
                <div key={`filler-${i}`} className="hidden bg-card lg:block" aria-hidden="true" />
              ))}
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}
